if (!customElements.get('blog-featured-section')) {
  class BlogFeaturedSection extends HTMLElement {
    constructor() {
      super();
      this._onBlockSelect = this._onBlockSelect.bind(this);
    }

    connectedCallback() {
      this._initTabs();
      this._bindVideos();
      this._flashItemVideos();
      document.addEventListener('shopify:block:select', this._onBlockSelect);
    }

    disconnectedCallback() {
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
    }

    // ── Tabs ──────────────────────────────────────────────────────────────

    _initTabs() {
      const tabs = this.querySelectorAll('[data-tab-index]');
      const panels = this.querySelectorAll('[data-tab-panel]');

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const targetIndex = tab.getAttribute('data-tab-index');

          tabs.forEach((btn) => btn.classList.remove('is-active'));
          tab.classList.add('is-active');

          panels.forEach((panel) => {
            const isActive = panel.getAttribute('data-tab-panel') === targetIndex;
            panel.classList.toggle('is-active', isActive);
            if (isActive) {
              panel.querySelectorAll('.blog-featured__fallback-video').forEach((video) => {
                if (video instanceof HTMLVideoElement) {
                  this._bindVideoToggle(video);
                }
              });
            }
          });
        });
      });
    }

    // ── Videos ────────────────────────────────────────────────────────────

    _bindVideos() {
      this.querySelectorAll('.blog-featured__fallback-video').forEach((video) => {
        if (video instanceof HTMLVideoElement) {
          this._bindVideoToggle(video);
        }
      });
    }

    _flashItemVideos() {
      this.querySelectorAll('.blog-featured__item-video').forEach((video) => {
        if (!(video instanceof HTMLVideoElement)) return;
        video.play().then(() => {
          setTimeout(() => {
            video.pause();
            video.currentTime = 0;
          }, 1000);
        }).catch(() => {});
      });
    }

    _createToggleButton() {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'blog-featured__video-toggle';
      button.dataset.state = 'playing';
      button.setAttribute('aria-label', 'Pause video');
      button.innerHTML = `
        <svg class="blog-featured__video-toggle-icon blog-featured__video-toggle-icon--play" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <path d="M7 4.5L17 11L7 17.5V4.5Z" fill="currentColor"/>
        </svg>
        <svg class="blog-featured__video-toggle-icon blog-featured__video-toggle-icon--pause" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
          <path d="M8 5V17M14 5V17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
        </svg>
      `;
      return button;
    }

    /**
     * @param {HTMLVideoElement} video
     */
    _bindVideoToggle(video) {
      if (video.dataset.blogVideoBound === 'true') return;

      video.dataset.blogVideoBound = 'true';
      video.setAttribute('tabindex', '0');
      video.setAttribute('role', 'button');
      video.setAttribute('aria-label', 'Play or pause video');

      const IMAGE_WRAP_SELECTOR = '.blog-featured__featured-image-wrap, .blog-featured__item-image-wrap';
      const wrap = video.closest(IMAGE_WRAP_SELECTOR);
      /** @type {HTMLButtonElement | null} */
      let toggleButton = null;

      if (wrap instanceof HTMLElement) {
        toggleButton = wrap.querySelector('.blog-featured__video-toggle');

        if (!(toggleButton instanceof HTMLButtonElement)) {
          toggleButton = this._createToggleButton();
          wrap.appendChild(toggleButton);
        }
      }

      const syncState = () => {
        if (!toggleButton) return;
        const isPaused = video.paused;
        toggleButton.dataset.state = isPaused ? 'paused' : 'playing';
        toggleButton.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
      };

      const togglePlayback = () => {
        if (video.paused) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
        syncState();
      };

      video.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        togglePlayback();
      });

      video.addEventListener('keydown', (event) => {
        if (event.code === 'Space' || event.code === 'Enter') {
          event.preventDefault();
          togglePlayback();
        }
      });

      if (toggleButton) {
        toggleButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          togglePlayback();
        });
      }

      video.addEventListener('play', syncState);
      video.addEventListener('pause', syncState);
      syncState();
    }

    // ── Shopify editor events ─────────────────────────────────────────────

    /**
     * @param {CustomEvent} event
     */
    _onBlockSelect(event) {
      if (!(event.target instanceof Element)) return;

      const tabButton = event.target.closest('[data-tab-index]');
      if (tabButton instanceof HTMLElement && this.contains(tabButton)) {
        tabButton.click();
      }
    }
  }

  customElements.define('blog-featured-section', BlogFeaturedSection);
}
